import { select } from 'd3-selection';
import { line } from 'd3-shape';
import _ from 'lodash';
import { calculatePixelSamples, calculateSamples, CONFIG_ECG } from '@/entities/ecg';
import { ANNOTATION_TYPE, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import { addSVGs } from '.';

export const calculateConfig = ({
  pGain,
  pSpeed,
  width,
  height,
  adcGain,
  ecgData,
  samplingRate,
  pLFF,
  pHFF,
}: {
  pGain?: number;
  pSpeed?: number;
  width: number;
  height: number;
  adcGain: number;
  ecgData: number[][];
  samplingRate: number;
  pLFF?: number;
  pHFF?: number;
}) => {
  const adcZero = 0;
  const speed = pSpeed || 25;
  const gain = pGain || 2;
  const dataLength = ecgData[0].length;
  const recDuration = dataLength / samplingRate;
  const aspectRatioX = width / recDuration / speed;
  const aspectRatioY = aspectRatioX;
  const secondsToPix = speed * aspectRatioX;
  const indexToPix = (speed * aspectRatioX) / samplingRate;
  const majorDivSize = width / recDuration;
  const millivoltToPix = gain * aspectRatioY;
  const LFF = !_.isNil(pLFF) ? pLFF : 1;
  const HFF = !_.isNil(pHFF) ? pHFF : 40;
  return {
    adcGain,
    dataLength,
    recDuration,
    aspectRatioX,
    aspectRatioY,
    secondsToPix,
    indexToPix,
    majorDivSize,
    millivoltToPix,
    LFF,
    HFF,
    adcZero,
    width,
    height,
    samplingRate,
  };
};

export const exportScroller = ({
  ecgData,
  channel = 'CH2',
}: {
  ecgData: number[];
  channel?: string;
}) => {
  const config = calculateConfig({
    pGain: ECG_VIEWER_CONFIG_STORE.ampValue || 10,
    pSpeed: 25,
    width: 945,
    height: 30,
    adcGain: CONFIG_ECG.gain,
    ecgData: [ecgData],
    samplingRate: CONFIG_ECG.samplingFrequency,
    pLFF: ECG_VIEWER_CONFIG_STORE.hiPValue,
    pHFF: ECG_VIEWER_CONFIG_STORE.loPValue,
  });
  const samples = calculateSamples([ecgData], config);
  const pixelSamples = calculatePixelSamples(samples, config);

  const svgElement = document.createElement('SVG');
  svgElement.setAttribute('style', 'border: 1px solid #0954BA');
  svgElement.setAttribute('width', '950');
  svgElement.setAttribute('height', '282');
  svgElement.setAttribute('version', '1.1');
  svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  if (svgElement) {
    const svg = select(svgElement);
    svg.selectAll('*').remove();
    const lineFunc = line<{ x: number; y: number }>()
      .x((d) => d.x)
      .y((d) => (d.y < 0 ? 250 : d.y + 250));
    // background + border
    svg
      .append('rect')
      .attr('x', 0)
      .attr('y', 250)
      .attr('width', 945)
      .attr('height', 32)
      .attr('stroke', '#0954BA')
      .attr('stroke-width', 1)
      .attr('fill', '#FFFFFF');

    pixelSamples.forEach((displayedSamples: { x: number; y: number }[]) => {
      svg
        .append('path')
        .attr('d', lineFunc(displayedSamples))
        .attr('stroke', '#1D2F47')
        .attr('stroke-width', '1.2px')
        .attr('fill', 'none');
    });

    svg
      .append('rect')
      .attr('x', 315)
      .attr('y', 250)
      .attr('width', 315)
      .attr('height', 32)
      .attr('fill', '#0954BA1A')
      .raise();

    svg
      .append('rect')
      .attr('x', 5)
      .attr('y', 258)
      .attr('width', 25)
      .attr('height', 16)
      .attr('fill', '#FFFFFF');
    svg
      .append('text')
      .attr('x', 5)
      .attr('y', 270)
      .attr('fill', '#828C99')
      .attr('font-size', '16px')
      .style('font-weight', '600')
      .style('font-family', 'Inconsolata')
      .text(channel);
  }

  return svgElement.outerHTML;
};

export const exportScrollers = ({
  exportEcgData,
  draw,
  allIndexStripSvg,
}: {
  exportEcgData: { ecgData: number[][] };
  draw: any[];
  allIndexStripSvg: string[];
}) => {
  const { ecgData } = exportEcgData || {};
  const sampleMark: any = _.find(draw, { type: ANNOTATION_TYPE.SAMPLE_MARK }) || {};
  const samplingRate = CONFIG_ECG.samplingFrequency * 10;
  const channel = sampleMark ? `CH${sampleMark.channelIndex + 1}` : 'CH2';
  const stripSvgs: string[] = [];
  const channelData = ecgData[sampleMark.channelIndex || 0];

  _.forEach(allIndexStripSvg, (strip: string, index: number) => {
    const startSample = sampleMark.startSample + index * samplingRate;
    const endSample = startSample + 3 * samplingRate;
    const sliceData = channelData.slice(startSample, endSample);
    const svg = exportScroller({ ecgData: sliceData, channel });
    const stripSvg = addSVGs([strip, svg], 282);
    stripSvgs.push(stripSvg);
  });
  return stripSvgs;
};

export default exportScroller;
